import { EmbeddingStatusBadge } from "@/components/common/embedding-status-badge";
import { GENRE_FEATURE_LABELS } from "@/components/enrichment/config";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { GenresReadType } from "@/pages/genres/schemas/genres.edit.form";
import { ReEnrichButton } from "@/pages/tina/components/re-enrich-button";

export function GenrePreview({
  genre,
  usedOn,
  usedOnArtists,
}: {
  genre: GenresReadType;
  usedOn: number;
  usedOnArtists: number;
}) {
  // Genre enrichment persists FLAT on the document, so the record itself is
  // what the shared summary reads: `about`, `features` and the rest.
  const enrichment = genre as unknown as Record<string, unknown>;

  return (
    <aside className="w-1/4 shrink-0 border-l border-border bg-card h-[calc(100vh-108px)] rounded-l-2xl">
      <div className="sticky top-0 flex flex-col h-full overflow-y-auto">
        {/* Header */}
        <div className="flex flex-col gap-2 p-5 pb-4">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-lg font-semibold leading-tight text-card-foreground text-balance">
              {genre.name}
            </h2>
            <div className="flex shrink-0 items-center gap-1">
              {/* Genre is one of the four EMBEDDABLE types; the embedding state
                  used to be invisible on every surface but /events. */}
              <EmbeddingStatusBadge
                status={genre.embeddingStatus}
                className="text-[10px] font-medium"
              />
            </div>
          </div>
          {genre.deprecated && (
            <Badge variant="destructive" className="w-fit text-[10px]">
              Deprecated
            </Badge>
          )}
          <EnrichmentSummary
            resource="genres"
            id={genre.id}
            status={genre.enrichmentStatus}
            enrichment={enrichment}
            featureLabels={GENRE_FEATURE_LABELS}
          />
        </div>

        <Separator />

        {/* Details */}
        <div className="flex flex-col gap-4 p-5">
          {/* Aliases */}
          {genre.aliases && genre.aliases.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Aliases
              </span>
              <div className="flex flex-wrap gap-1">
                {genre.aliases.map((alias) => (
                  <Badge
                    key={alias}
                    variant="secondary"
                    className="text-[11px] font-normal"
                  >
                    {alias}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Parents */}
          {genre.parents && genre.parents.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Parents
              </span>
              <div className="flex flex-wrap gap-1">
                {genre.parents.map((parent) => (
                  <Badge
                    key={parent}
                    variant="outline"
                    className="text-[11px] font-normal"
                  >
                    {parent}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Audio features move with the AI Enrichment card above: the card
              draws every scored dimension, strongest first. */}

          <Separator />

          {/* Metadata */}
          {genre.metadata && (
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Metadata
              </span>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <span className="text-muted-foreground">Source</span>
                <span className="text-card-foreground truncate">
                  {genre.metadata.source}
                </span>
                <span className="text-muted-foreground">Provider</span>
                <span className="text-card-foreground truncate">
                  {genre.metadata.provider}
                </span>
              </div>
              {genre.metadata.urls && genre.metadata.urls.length > 0 && (
                <div className="flex flex-col gap-1 mt-1">
                  {genre.metadata.urls.map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-primary underline underline-offset-2 truncate hover:text-primary/80"
                    >
                      {url}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          <Separator />

          {/* Transliterations */}
          {genre.transliterations && (
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Transliterations
              </span>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                {Object.entries(genre.transliterations).map(
                  ([lang, values]) => (
                    <div
                      key={lang}
                      className="col-span-2 flex items-start gap-3"
                    >
                      <span className="w-5 shrink-0 font-mono text-[11px] uppercase text-muted-foreground">
                        {lang}
                      </span>
                      <span className="text-card-foreground">
                        {values?.join(", ")}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
          {/* Re-enrich. Genre gets NO Tina Trace card: its enrichment persists
              flat on the genre document, which has no `taskName`, so
              `UpdateGenreEnrichmentCommand` accepts the value and deliberately
              drops it. There is nothing to point at, and the
              queue button is what a curator actually needs here. */}
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              AI Enrichment
            </span>
            <ReEnrichButton
              entityType="genre"
              entityId={genre.id}
              className="w-full"
            />
          </div>

          <Separator />

          {/* Usage */}
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Usage
            </span>
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              <span className="text-muted-foreground">Events</span>
              <span className="text-card-foreground">{usedOn}</span>
              <span className="text-muted-foreground">Artists</span>
              <span className="text-card-foreground">{usedOnArtists}</span>
            </div>
          </div>
        </div>

        {/* Footer ID */}
        <div className="mt-auto border-t border-border p-4">
          <span className="font-mono text-[10px] text-muted-foreground">
            ID: {genre.id}
          </span>
        </div>
      </div>
    </aside>
  );
}
